import BigNumber from 'bignumber.js'
import { match } from 'ts-pattern'

import * as Network from './network'

import type { ServicesApi } from '@root/shared/api'

export type NetworkData = {
  network: Network.Network
  nativePrice: BigNumber
  priorityFees: {
    /**
     * ETH|BASE in GWEI, SOL in SOLANA
     */
    recommended: BigNumber
    medium: BigNumber
  }
}

export type NetworkDataSol = NetworkData & {
  jitoAverageTips: {
    recommended: BigNumber
    medium: BigNumber
  }
}

export type NetworkDataByNetwork = {
  [Network.Ethereum]: NetworkData
  [Network.Base]: NetworkData
  [Network.Solana]: NetworkDataSol
  [Network.Ton]: NetworkData
  [Network.Bsc]: NetworkData
}

export const fromDto = (
  dto: ServicesApi.NetworkDataDto,
): NetworkDataByNetwork => {
  const mapNetworkData = <const T extends Network.Network>(
    network: T,
  ): NetworkDataByNetwork[T] => {
    const networkDto = Network.toDto(network)

    type NativePriceKeyDto = keyof ServicesApi.NetworkDataDto['nativePrices']
    const nativePriceKey = match(networkDto)
      .returnType<NativePriceKeyDto>()
      .with('base', () => 'eth')
      .with('eth', () => 'eth')
      .with('bsc', () => 'bsc')
      .with('sol', () => 'sol')
      .with('ton', () => 'ton')
      .exhaustive()

    const priorityFees = dto.priorityFees[networkDto]
    const base: NetworkData = {
      network,
      nativePrice: BigNumber(dto.nativePrices[nativePriceKey]),
      priorityFees: {
        recommended: BigNumber(priorityFees.recommended.formatted),
        medium: BigNumber(priorityFees.medium.formatted),
      },
    }

    if (network === Network.Solana) {
      return {
        ...base,
        jitoAverageTips: {
          // native - wei | lamport, formatted - SOL ETH
          recommended: BigNumber(dto.jitoAverageTips.recommended.formatted),
          medium: BigNumber(dto.jitoAverageTips.medium.formatted),
        },
      }
    }

    return base as NetworkDataByNetwork[T]
  }

  return {
    [Network.Ethereum]: mapNetworkData(Network.Ethereum),
    [Network.Base]: mapNetworkData(Network.Base),
    [Network.Solana]: mapNetworkData(Network.Solana),
    [Network.Ton]: mapNetworkData(Network.Ton),
    [Network.Bsc]: mapNetworkData(Network.Bsc),
  }
}
